
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../../context/AuthContext'; // Ajusta la ruta si es necesario
import { API_BASE_URL } from '../../../../constants'; // Ajusta la ruta si es necesario

const SupportListInventory = ({ data, onInventoryUpdate }) => {
    const { t } = useTranslation();
    const { isAuthenticated, loading: authLoading, token } = useAuth(); // Anti-anxiety loading check

    // UI States
    const [isExpanded, setIsExpanded] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [showLoginModal, setShowLoginModal] = useState(false); // Native Login UI

    // Data States
    const [inventory, setInventory] = useState({}); // { [id]: cap }
    const [serverInventory, setServerInventory] = useState({}); // Snapshot for diff
    const [saving, setSaving] = useState(false);

    // Feedback
    const [feedbackMsg, setFeedbackMsg] = useState('');
    const [showUndoConfirm, setShowUndoConfirm] = useState(false);

    const searchContainerRef = useRef(null);

    // Click Outside to Clear Search
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (searchContainerRef.current && !searchContainerRef.current.contains(event.target)) {
                setSearchQuery('');
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const fetchInventory = useCallback(async () => {
        try {
            if (!token) return;

            const response = await fetch(`${API_BASE_URL}/api/user/inventory`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (response.ok) {
                const resData = await response.json();
                const invData = resData.data || {};
                setInventory(invData);
                setServerInventory(JSON.parse(JSON.stringify(invData))); // Deep copy snapshot
            } else {
                console.error("Failed to fetch inventory");
            }
        } catch (error) {
            console.error("Network error fetching inventory", error);
        }
    }, [token]);

    // --- 1. Load Inventory on Mount ---
    useEffect(() => {
        if (isAuthenticated) {
            fetchInventory();
        }
    }, [isAuthenticated, fetchInventory]);

    // --- 2. Save Logic ---
    const hasChanges = useMemo(() => {
        return JSON.stringify(inventory) !== JSON.stringify(serverInventory);
    }, [inventory, serverInventory]);

    const handleSave = async () => {
        if (!hasChanges) return;
        setSaving(true);
        setFeedbackMsg('');

        try {
            const response = await fetch(`${API_BASE_URL}/api/user/inventory`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ data: inventory })
            });

            if (response.ok) {
                setServerInventory(JSON.parse(JSON.stringify(inventory))); // Update snapshot
                setFeedbackMsg(t('Saved successfully!')); // TODO: Add translation key later
                setTimeout(() => setFeedbackMsg(''), 3000);

                // NOTIFY PARENT TO REFRESH
                if (onInventoryUpdate) {
                    onInventoryUpdate();
                }
            } else {
                setFeedbackMsg('Error saving');
            }
        } catch (error) {
            setFeedbackMsg('Network error');
        } finally {
            setSaving(false);
        }
    };

    // --- 3. Interaction Logic ---

    // Sorting State
    const [sortConfig, setSortConfig] = useState({
        key: 'rarity', // 'name' | 'cap' | 'rarity'
        direction: 'asc' // 'asc' (Standard User Def) | 'desc' (Inverted)
    });

    // Toggle Sort Helper
    const handleSortChange = (key) => {
        setSortConfig(prev => {
            if (prev.key === key) {
                // If same key, toggle direction
                return { ...prev, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
            }
            return { key, direction: 'asc' }; // Reset to standard on key change
        });
    };

    const toggleDirection = () => {
        setSortConfig(prev => ({ ...prev, direction: prev.direction === 'asc' ? 'desc' : 'asc' }));
    };

    // Helper: Validar si existe en Matrix
    const allCards = useMemo(() => {
        if (!data || !data.matrix) return [];
        // STANDARD PATTERN: Inject Key as top-level 'id'
        // matches SupportComparator.js line 67
        return Object.entries(data.matrix).map(([key, card]) => ({
            ...card,
            id: Number(key) // The Key IS the ID
        }));
    }, [data]);

    // Search Filter
    const filteredCards = useMemo(() => {
        if (!searchQuery.trim()) return [];
        const lowerQ = searchQuery.toLowerCase();
        return allCards.filter(card =>
            card.info &&
            card.info.char_name &&
            card.info.char_name.toLowerCase().includes(lowerQ)
        ).slice(0, 20); // Limit results for performance
    }, [searchQuery, allCards]);

    // Inventory List (Cards I have) - RAW
    const rawInventoryList = useMemo(() => {
        if (!data || !data.matrix) return [];
        // Map inventory IDs back to full card objects
        return Object.entries(inventory).map(([keyId, cap]) => {
            // Lookup by ID (Standard)
            const card = data.matrix[keyId] || Object.values(data.matrix).find(c => c.support_id === Number(keyId));
            if (!card) return null;

            // STANDARD PATTERN: Ensure 'id' is set from the key/source
            return {
                ...card,
                userCap: cap,
                id: Number(keyId) // Trusted ID source
            };
        }).filter(c => c !== null);
    }, [inventory, data]);

    // Sorted Inventory List
    const inventoryList = useMemo(() => {
        // Defines Reference Priority (Lower Value = First in "Asc/Standard" Order)
        const rarityWeight = { 'SSR': 1, 'SR': 2, 'R': 3, 'Unknown': 4 };

        const sorted = [...rawInventoryList].sort((a, b) => {
            const nameA = a.info?.char_name || '';
            const nameB = b.info?.char_name || '';
            const rarA = rarityWeight[a.info?.rarity] || 99;
            const rarB = rarityWeight[b.info?.rarity] || 99;
            const capA = a.userCap;
            const capB = b.userCap;

            // Comparator Logic based on User Rules (Standard Order)
            let comparison = 0;

            if (sortConfig.key === 'name') {
                // 1. Name (A-Z)
                comparison = nameA.localeCompare(nameB);
                // 2. Rarity (SSR -> SR -> R)
                if (comparison === 0) comparison = rarA - rarB;
            }
            else if (sortConfig.key === 'cap') {
                // 1. Cap (High -> Low)
                comparison = capB - capA;
                // 2. Name (A-Z)
                if (comparison === 0) comparison = nameA.localeCompare(nameB);
                // 3. Rarity (SSR -> SR -> R)
                if (comparison === 0) comparison = rarA - rarB;
            }
            else if (sortConfig.key === 'rarity') {
                // 1. Rarity (SSR -> SR -> R)
                comparison = rarA - rarB;
                // 2. Name (A-Z)
                if (comparison === 0) comparison = nameA.localeCompare(nameB);
            }

            return comparison;
        });

        // Apply Global Invert if 'desc'
        if (sortConfig.direction === 'desc') {
            return sorted.reverse();
        }

        return sorted;
    }, [rawInventoryList, sortConfig]);

    const handleRemoveCard = (cardId) => {
        setInventory(prev => {
            const next = { ...prev };
            delete next[cardId];
            return next;
        });
    };

    const handleSetCap = (cardId, capLevel) => {
        setInventory(prev => ({
            ...prev,
            [cardId]: capLevel
        }));
    };

    const handleUndo = () => {
        if (!hasChanges) return;
        setShowUndoConfirm(true);
    };

    const confirmUndo = () => {
        setInventory(JSON.parse(JSON.stringify(serverInventory)));
        setShowUndoConfirm(false);
    };

    const cancelUndo = () => {
        setShowUndoConfirm(false);
    };

    // Helper for Type Icon path (Copied from SupportTable.js for consistency)
    const getTypeIconPath = (typeStr) => {
        if (!typeStr) return '';
        const type = typeStr.charAt(0).toUpperCase() + typeStr.slice(1).toLowerCase();
        if (['Speed', 'Stamina', 'Power', 'Guts', 'Wit'].includes(type)) {
            return `/images/Icon/Stats_icons/Stat_Type_${type}.png`;
        } else if (['Pal', 'Group'].includes(type)) {
            return `/images/Icon/Support_Icons/Support_Type_${type}.png`;
        }
        return '';
    };

    // --- 4. Render Logic ---
    const handleBannerClick = () => {
        if (authLoading) return; // Anti-anxiety: Do nothing while checking auth

        if (!isAuthenticated) {
            setShowLoginModal(true); // Native Interface instead of alert
            return;
        }
        setIsExpanded(!isExpanded);
    };

    return (
        <div className={`w-full bg-white dark:bg-slate-800 rounded-xl shadow-sm mb-6 border border-slate-200 dark:border-slate-700 transition-all duration-300 ${isExpanded ? 'overflow-visible' : 'overflow-hidden'} relative`}>

            {/* Header / Banner */}
            <div
                onClick={handleBannerClick}
                className={`w-full p-4 flex items-center justify-between cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors ${authLoading ? 'opacity-70 cursor-wait' : ''}`}
            >
                <div>
                    <h2 className="text-xl font-bold text-slate-800 dark:text-white flex items-center gap-2">
                        {authLoading ? (
                            <svg className="animate-spin h-5 w-5 text-slate-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                        ) : (
                            <span>🗃️</span>
                        )}
                        {t('My Inventory')}
                    </h2>
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                        {authLoading
                            ? "Verifying session..."
                            : (isAuthenticated
                                ? `${Object.keys(inventory).length} cards stored`
                                : "Login to manage your cards")}
                    </p>
                </div>
                {!authLoading && (
                    <svg
                        className={`w-6 h-6 text-slate-400 transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`}
                        fill="none" viewBox="0 0 24 24" stroke="currentColor"
                    >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                )}
            </div>

            {/* Native Login Modal (Overlay) */}
            {showLoginModal && (
                <div className="absolute top-full left-0 w-full mt-2 bg-slate-800 text-white p-6 rounded-xl shadow-2xl border border-orange-500 z-50 animate-in fade-in slide-in-from-top-4">
                    <div className="flex justify-between items-start mb-4">
                        <h3 className="text-xl font-bold text-orange-400">Login Required</h3>
                        <button onClick={(e) => { e.stopPropagation(); setShowLoginModal(false); }} className="text-slate-400 hover:text-white">✕</button>
                    </div>
                    <p className="mb-4 text-slate-300">You need to be logged in to manage your inventory and save your decks.</p>
                    <div className="flex gap-2 justify-end">
                        <a href="/login" className="px-4 py-2 bg-orange-500 hover:bg-orange-600 rounded text-white font-bold text-sm">Login / Register</a>
                        <button onClick={(e) => { e.stopPropagation(); setShowLoginModal(false); }} className="px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded text-slate-300 text-sm">Close</button>
                    </div>
                </div>
            )}

            {/* Content Permission Gate */}
            {isAuthenticated && (
                <div className={`transition-all duration-300 ease-in-out ${isExpanded ? 'max-h-[2000px] opacity-100 overflow-visible' : 'max-h-0 opacity-0 overflow-hidden'}`}>
                    <div className="p-4 border-t border-slate-200 dark:border-slate-700">

                        {/* Toolbar: Search + Save */}
                        <div className="flex flex-col md:flex-row gap-4 mb-6 justify-between items-start md:items-center">

                            {/* Left Group: Undo + Search */}
                            <div className="flex items-center gap-2 w-full md:w-1/2 z-30">
                                {/* Undo Button (Always Visible, Disabled if no changes) */}
                                <div className="relative">
                                    <button
                                        onClick={handleUndo}
                                        disabled={!hasChanges}
                                        className={`p-2 rounded-lg transition-all ${hasChanges
                                            ? 'text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 cursor-pointer'
                                            : 'text-slate-300 dark:text-slate-600 cursor-not-allowed'}`}
                                        title={hasChanges ? "Undo Changes" : "No changes to undo"}
                                    >
                                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                                        </svg>
                                    </button>

                                    {/* Custom Confirmation Overlay (Absolute within relative parent) */}
                                    {showUndoConfirm && (
                                        <div className="absolute top-full left-0 mt-2 w-64 bg-white dark:bg-slate-800 p-4 rounded-xl shadow-2xl border-2 border-red-500/50 z-50 animate-in fade-in zoom-in-95 duration-200">
                                            <p className="text-sm font-bold text-slate-800 dark:text-white mb-3">
                                                Discard unsaved changes?
                                            </p>
                                            <div className="flex gap-2 justify-end">
                                                <button
                                                    onClick={cancelUndo}
                                                    className="px-3 py-1 text-xs font-bold text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700 rounded"
                                                >
                                                    Cancel
                                                </button>
                                                <button
                                                    onClick={confirmUndo}
                                                    className="px-3 py-1 text-xs font-bold text-white bg-red-500 hover:bg-red-600 rounded shadow-sm"
                                                >
                                                    Discard
                                                </button>
                                            </div>
                                            {/* Arrow Indicator */}
                                            <div className="absolute -top-2 left-3 w-4 h-4 bg-white dark:bg-slate-800 border-t-2 border-l-2 border-red-500/50 transform rotate-45"></div>
                                        </div>
                                    )}
                                </div>

                                {/* Search */}
                                <div className="relative flex-1" ref={searchContainerRef}>
                                    <input
                                        type="text"
                                        placeholder="Search card to add..."
                                        className="w-full px-4 py-2 rounded-lg bg-slate-100 dark:bg-slate-900 border border-slate-300 dark:border-slate-600 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:text-white"
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                    />
                                    {/* Dropdown Results */}
                                    {searchQuery && (
                                        <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-xl max-h-80 overflow-y-auto z-50">
                                            {filteredCards.length > 0 ? (
                                                filteredCards.map(card => {
                                                    const isOwned = !!inventory[card.id];
                                                    return (
                                                        <div
                                                            key={card.id}
                                                            onClick={() => {
                                                                if (!isOwned) {
                                                                    handleSetCap(card.id, 0);
                                                                    setSearchQuery('');
                                                                }
                                                            }}
                                                            className={`p-3 border-b border-slate-100 dark:border-slate-700/50 flex flex-col gap-2 transition-colors
                                                                ${isOwned
                                                                    ? 'bg-slate-50 dark:bg-slate-900/50 cursor-default opacity-75'
                                                                    : 'hover:bg-slate-50 dark:hover:bg-slate-700 cursor-pointer'}`}
                                                        >
                                                            {/* Row Top: Image + Name */}
                                                            <div className="flex items-center gap-3">
                                                                <div className="relative">
                                                                    <img
                                                                        src={`${API_BASE_URL}/api/thumb?img=/images/Support/Card/Support_Card_${card.id}_Card.png&width=64`}
                                                                        alt="Icon"
                                                                        className={`w-10 h-10 rounded shadow-sm object-cover ${isOwned ? 'grayscale' : ''}`}
                                                                    />
                                                                    {isOwned && (
                                                                        <div className="absolute -top-1 -right-1 bg-green-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full shadow-sm">
                                                                            ✔
                                                                        </div>
                                                                    )}
                                                                </div>

                                                                <div className="flex-1 min-w-0">
                                                                    <div className="text-xs font-bold text-slate-500 uppercase">{card.info?.rarity} • {card.info?.Support_Type}</div>
                                                                    <div className={`text-sm font-bold truncate ${isOwned ? 'text-slate-400 line-through' : 'text-slate-800 dark:text-white'}`}>
                                                                        {card.info?.char_name}
                                                                    </div>
                                                                </div>
                                                            </div>

                                                            {/* Row Bottom: Quick Add Buttons OR Status Message */}
                                                            <div className="self-start">
                                                                {isOwned ? (
                                                                    <span className="text-xs font-bold text-emerald-500 bg-emerald-100 dark:bg-emerald-900/30 px-2 py-1 rounded inline-flex items-center gap-1">
                                                                        <span>✅</span> Already in Inventory
                                                                    </span>
                                                                ) : (
                                                                    <div className="flex items-center gap-2 bg-slate-100 dark:bg-slate-900/50 rounded-lg p-1">
                                                                        <span className="text-[10px] text-slate-400 font-bold px-2">ADD:</span>
                                                                        {[0, 1, 2, 3, 4].map((capLevel) => (
                                                                            <button
                                                                                key={capLevel}
                                                                                onClick={(e) => {
                                                                                    e.stopPropagation();
                                                                                    handleSetCap(card.id, capLevel);
                                                                                    setSearchQuery(''); // Close on add
                                                                                }}
                                                                                className="hover:scale-125 transition-transform p-1"
                                                                            >
                                                                                {capLevel === 0 ? (
                                                                                    <span className="text-xs font-bold text-slate-500 px-1">Base</span>
                                                                                ) : (
                                                                                    <img
                                                                                        src="/images/Icon/Resources/CrystalCap.png"
                                                                                        alt={`Cap ${capLevel}`}
                                                                                        className="w-4 h-auto"
                                                                                    />
                                                                                )}
                                                                            </button>
                                                                        ))}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    );
                                                })
                                            ) : (
                                                <div className="p-3 text-sm text-slate-500 text-center">No cards found</div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Save Button */}
                            <div className="flex items-center gap-2">
                                <span className={`text-sm font-medium ${feedbackMsg.includes('Error') ? 'text-red-500' : 'text-green-500'}`}>
                                    {feedbackMsg}
                                </span>
                                <button
                                    onClick={handleSave}
                                    disabled={!hasChanges || saving}
                                    className={`px-6 py-2 rounded-lg font-bold transition-all shadow-md
                                        ${hasChanges
                                            ? 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white hover:shadow-lg hover:scale-105 active:scale-95'
                                            : 'bg-slate-200 dark:bg-slate-700 text-slate-400 cursor-not-allowed'}
                                    `}
                                >
                                    {saving ? 'Saving...' : 'Save Changes'}
                                </button>
                            </div>
                        </div>

                        {/* --- Sorting Toolbar --- */}
                        <div className="flex items-center gap-2 mb-4 overflow-x-auto pb-2 custom-scrollbar">
                            <span className="text-xs font-bold text-slate-400 uppercase mr-2">Sort by:</span>

                            {/* Sort Buttons */}
                            {['name', 'cap', 'rarity'].map(key => (
                                <button
                                    key={key}
                                    onClick={() => handleSortChange(key)}
                                    className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all border
                                        ${sortConfig.key === key
                                            ? 'bg-blue-100 text-blue-600 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-800'
                                            : 'bg-white text-slate-500 border-slate-200 hover:bg-slate-50 dark:bg-slate-800 dark:text-slate-400 dark:border-slate-700 dark:hover:bg-slate-700'}
                                    `}
                                >
                                    {key === 'name' ? 'Name' : key === 'cap' ? 'Limit Break' : 'Rarity'}
                                </button>
                            ))}

                            <div className="w-px h-4 bg-slate-300 dark:bg-slate-600 mx-1"></div>

                            {/* Direction Toggle */}
                            <button
                                onClick={toggleDirection}
                                className="px-3 py-1.5 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 dark:bg-slate-800 dark:border-slate-700 dark:hover:bg-slate-700 dark:text-slate-300 text-slate-600 transition-colors"
                                title={sortConfig.direction === 'asc' ? "Switch to Inverted Order" : "Switch to Standard Order"}
                            >
                                {sortConfig.direction === 'asc' ? (
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M3 4.5h14.25M3 9h9.75M3 13.5h5.25m5.25-.75L17.25 9m0 0L21 12.75M17.25 9v12" />
                                    </svg>
                                ) : (
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M3 4.5h14.25M3 9h9.75M3 13.5h9.75m4.5-4.5v12m0 0l-3.75-3.75M17.25 21L21 17.25" />
                                    </svg>
                                )}
                            </button>
                        </div>

                        {/* Inventory Grid Container - Scrollable */}
                        <div className="max-h-[650px] overflow-y-auto custom-scrollbar pr-2 -mr-2 p-4">
                            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-4 pb-2">
                                {inventoryList.map((card) => (
                                    <div key={card.id} className="relative group flex flex-col items-center p-2 bg-slate-50 dark:bg-slate-900/30 rounded-xl border border-slate-100 dark:border-slate-800 hover:border-blue-300 dark:hover:border-blue-700 transition-colors">

                                        {/* Remove Button (Always Visible now, better positioned) */}
                                        <button
                                            onClick={() => handleRemoveCard(card.id)}
                                            className="absolute -top-2 -right-2 bg-red-500 hover:bg-red-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs shadow-md z-20 transition-transform hover:scale-110"
                                            title="Remove card"
                                        >
                                            ✕
                                        </button>

                                        {/* Card Image */}
                                        <div
                                            onClick={() => handleSetCap(card.id, 0)}
                                            className="relative w-full aspect-[3/4] rounded-lg overflow-hidden border border-slate-200 dark:border-slate-700 shadow-sm cursor-pointer mb-2 group"
                                        >
                                            <img
                                                src={`${API_BASE_URL}/api/thumb?img=/images/Support/Card/Support_Card_${card.id}_Card.png&width=160`}
                                                alt={card.info?.char_name || "Card"}
                                                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                                                loading="lazy"
                                            />
                                            {/* Type Icon Overlay (Replaces old text) */}
                                            <img
                                                src={getTypeIconPath(card.info?.Support_Type)}
                                                className="absolute bottom-1 right-1 w-5 h-5 drop-shadow-md transition-transform duration-500 origin-bottom-right group-hover:scale-110"
                                                alt={card.info?.Support_Type}
                                            />
                                        </div>

                                        {/* Info: Rarity + Name */}
                                        <div className="w-full text-center mb-2 min-h-[2.5rem] flex flex-col justify-center">
                                            <div className={`text-[10px] font-black uppercase tracking-wider
                                                ${card.info?.rarity === 'SSR' ? 'text-transparent bg-clip-text bg-gradient-to-r from-yellow-500 to-orange-500' :
                                                    card.info?.rarity === 'SR' ? 'text-yellow-600' : 'text-slate-500'}
                                            `}>
                                                {card.info?.rarity || '???'}
                                            </div>
                                            <div className="text-xs font-bold text-slate-700 dark:text-slate-300 line-clamp-2 leading-tight">
                                                {card.info?.char_name}
                                            </div>
                                        </div>

                                        {/* Crystals */}
                                        <div className="flex gap-0.5 bg-white dark:bg-slate-800 rounded-full px-2 py-1 border border-slate-200 dark:border-slate-600 shadow-inner">
                                            {[0, 1, 2, 3].map((capIndex) => {
                                                const isActive = (capIndex + 1) <= card.userCap;
                                                return (
                                                    <img
                                                        key={capIndex}
                                                        src="/images/Icon/Resources/CrystalCap.png"
                                                        alt={`Cap ${capIndex + 1}`}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            // Toggle Logic: If clicking current cap, reset to 0
                                                            const targetCap = capIndex + 1;
                                                            const newCap = (card.userCap === targetCap) ? 0 : targetCap;
                                                            handleSetCap(card.id, newCap);
                                                        }}
                                                        className={`w-4 h-auto cursor-pointer transition-all hover:scale-125 ${isActive ? '' : 'grayscale opacity-20'}`}
                                                        title={`Set Cap ${capIndex + 1}`}
                                                    />
                                                );
                                            })}
                                        </div>
                                    </div>
                                ))}

                                {/* Empty State Hint */}
                                {inventoryList.length === 0 && (
                                    <div className="col-span-full py-8 text-center text-slate-400 border-2 border-dashed border-slate-200 dark:border-slate-700 rounded-xl">
                                        <p>Your inventory is empty.</p>
                                        <p className="text-xs mt-1">Search for cards above to add them.</p>
                                    </div>
                                )}
                            </div>
                        </div>

                    </div>
                </div>
            )}
        </div>
    );
};

export default SupportListInventory;
